import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import CRMHeader from './CRMHeader';
import type { CRMHeaderProps } from './CRMHeader';
import type { CrmQuickAction, CrmMetric } from './types';

export interface CrmOutletContext {
  activeOrgId: string | null;
  setHeaderProps: (props?: Partial<CRMHeaderProps>) => void;
  setQuickActions: (actions: CrmQuickAction[]) => void;
  setMetrics: (metrics: CrmMetric[]) => void;
}

const CrmLayoutContext = createContext<CrmOutletContext | null>(null);

export const useCrmLayout = (): CrmOutletContext => {
  const value = useContext(CrmLayoutContext);
  if (!value) {
    throw new Error('useCrmLayout must be called within a CRMLayout');
  }
  return value;
};

interface NavItem {
  to: string;
  label: string;
  badge: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/crm/dashboards', label: 'Dashboards', badge: 'DB' },
  { to: '/crm/contacts', label: 'Contacts', badge: 'CT' },
  { to: '/crm/companies', label: 'Companies', badge: 'CO' },
  { to: '/crm/deals', label: 'Deals', badge: 'DE' },
  { to: '/crm/activities', label: 'Activities', badge: 'AC' },
  { to: '/crm/calendar', label: 'Calendar', badge: 'CA' },
  { to: '/crm/workflows', label: 'Workflows', badge: 'WF' },
  { to: '/crm/telemarketing', label: 'Telemarketing', badge: 'TM' },
];

const CRMLayout: React.FC = () => {
  const { activeOrgId, isBootstrapped } = useCrmOrgStore();
  const location = useLocation();
  const defaultHeader = useMemo<CRMHeaderProps>(() => ({
    title: 'CRM',
    subtitle: 'Manage your workspace data',
    showNavigation: false,
  }), []);
  const [headerProps, setHeaderPropsState] = useState<CRMHeaderProps>(defaultHeader);
  const [quickActions, setQuickActions] = useState<CrmQuickAction[]>([]);
  const [metrics, setMetrics] = useState<CrmMetric[]>([]);

  const setHeaderProps = useCallback((props?: Partial<CRMHeaderProps>) => {
    setHeaderPropsState(props ? { ...defaultHeader, ...props } : defaultHeader);
  }, [defaultHeader]);

  useEffect(() => {
    setQuickActions([]);
    setMetrics([]);
  }, [location.pathname]);

  if (!isBootstrapped) {
    return (
      <div className="space-y-4">
        <CRMHeader showNavigation={false} subtitle="Loading your workspace…" />
        <div className="bg-white rounded shadow p-6 flex items-center justify-center text-gray-500">
          <span className="animate-pulse">Preparing your CRM workspace…</span>
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="space-y-4">
        <CRMHeader showNavigation={false} subtitle="Create or select an organization to continue." />
        <div className="bg-white rounded shadow p-6 text-center text-gray-600">
          <p className="text-sm">
            We couldn't resolve an active organization for your account. Use the selector above to create or
            choose a workspace before continuing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <CrmLayoutContext.Provider value={{ activeOrgId, setHeaderProps, setQuickActions, setMetrics }}>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[250px,1fr] lg:items-start lg:gap-8">
        <aside className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-400">Workspace</div>
          <nav className="flex flex-col gap-1 text-sm font-medium">
            {NAV_ITEMS.map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                isActive={location.pathname.startsWith(item.to)}
              />
            ))}
          </nav>
        </aside>

        <div className="flex flex-1 flex-col gap-6">
          <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <CRMHeader {...headerProps} />
          </section>

          {quickActions.length > 0 && (
            <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-700">Quick actions</h2>
                <span className="text-xs uppercase tracking-wide text-gray-400">{quickActions.length} shortcuts</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={getActionClasses(action)}
                  >
                    <span className="text-sm font-medium">{action.label}</span>
                    {action.description ? (
                      <span className="text-xs text-gray-500">{action.description}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>
          )}

          {metrics.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700">Key metrics</h2>
                <span className="text-xs uppercase tracking-wide text-gray-400">{metrics.length} insights</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {metrics.map((metric) => (
                  <div
                    key={metric.id}
                    className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">{metric.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900">{metric.value}</p>
                    {metric.change ? (
                      <p className={getChangeClasses(metric.change.direction)}>
                        {metric.change.text}
                      </p>
                    ) : null}
                    {metric.hint ? (
                      <p className="mt-2 text-xs text-gray-500">{metric.hint}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="space-y-6">
            <Outlet />
          </div>
        </div>
      </div>
    </CrmLayoutContext.Provider>
  );
};

export default CRMLayout;
export { CrmLayoutContext };

const getActionClasses = (action: CrmQuickAction) => {
  if (action.disabled) {
    return 'inline-flex min-h-[56px] cursor-not-allowed flex-col justify-center gap-1 rounded-xl border border-gray-200/80 bg-gray-50 px-4 py-3 text-left text-gray-400 shadow-sm';
  }

  const base = 'inline-flex min-h-[56px] flex-col justify-center gap-1 rounded-xl border px-4 py-3 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

  switch (action.tone) {
    case 'secondary':
      return `${base} border-secondary/20 bg-secondary/5 text-secondary hover:border-secondary/40 hover:bg-secondary/10`;
    case 'danger':
      return `${base} border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100`;
    default:
      return `${base} border-primary/20 bg-primary/5 text-primary hover:border-primary/40 hover:bg-primary/10`;
  }
};

const getChangeClasses = (direction: 'up' | 'down' | 'flat') => {
  if (direction === 'up') return 'mt-3 text-xs font-semibold text-green-600';
  if (direction === 'down') return 'mt-3 text-xs font-semibold text-red-600';
  return 'mt-3 text-xs font-semibold text-gray-500';
};

interface SidebarLinkProps extends NavItem {
  isActive: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, label, badge, isActive }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 rounded-xl px-3 py-2 transition ${
      isActive
        ? 'bg-primary/10 text-primary shadow-sm'
        : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
    }`}
  >
    <span
      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
        isActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {badge}
    </span>
    <span className="flex-1">{label}</span>
  </Link>
);
